import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { post, type ApiData } from '@app/api/typed';
import type { SimulateRequest } from './SimulatorModel';

export type SimulateResult = ApiData<'/api/v1/simulate', 'post'>;

/**
 * O alvo perguntado — a tool, ou o agente no modo A2A (DEF-29, 09/09).
 *
 * A resposta do `simulate` **não** o ecoa, e sem ele o bloco de resultado não
 * saberia sobre o que a negação fala: «O que faria passar» mandaria acrescentar
 * a delegação a Identity Access › Tools, que é o conselho errado.
 */
export type SimulationTarget =
  | { kind: 'tool'; tool: string; server?: string }
  | { kind: 'agent'; agent: string };

/** O que a tela mostra: o resultado + o instante em que ele foi obtido. */
export interface SimulationDatada {
  resultado: SimulateResult;
  /** Quando a resposta chegou (relógio do navegador). */
  avaliadoEm: Date;
  /**
   * O alvo da pergunta, e **só** ele: o `token` da pessoa não sobe para cá.
   * Guardar o corpo inteiro seria manter o JWT em estado da tela por conveniência
   * — pela mesma razão que esta chamada é `useMutation` e não `useQuery`.
   */
  target: SimulationTarget;
}

/**
 * Simular uma chamada (T18, FR-100). **Não** gera decisão real nem auditoria de
 * mutação (R11).
 *
 * É `useMutation` e não `useQuery` de propósito, por duas razões:
 *
 * 1. o `token` da pessoa **não pode entrar em cache** — chave de query é estado
 *    persistente da sessão, e um JWT não tem o que fazer ali;
 * 2. a resposta é uma afirmação sobre **agora**: o documento do PDP muda a cada
 *    `contract.apply` e `policy.publish`, então um resultado guardado envelhece
 *    em silêncio. Revalidar por conta própria seria pior — o operador leria um
 *    veredito novo sem ter pedido.
 *
 * Daí o `avaliadoEm`: o resultado é um fato **datado**, e a tela diz a data.
 */
export function useSimulate(): UseMutationResult<SimulationDatada, ApiError, SimulateRequest> {
  return useMutation<SimulationDatada, ApiError, SimulateRequest>({
    mutationFn: async (body) => {
      const resultado = await post('/api/v1/simulate', { body });
      const target: SimulationTarget = body.agent
        ? { kind: 'agent', agent: body.agent }
        : { kind: 'tool', tool: body.tool ?? '', ...(body.server ? { server: body.server } : {}) };
      return { resultado, avaliadoEm: new Date(), target };
    },
  });
}
